import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Volume2, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const SHAPES = [
  { name: "دائرة", svg: (color: string) => <circle cx="50" cy="50" r="40" fill={color} />, nameEn: "circle" },
  { name: "مربع", svg: (color: string) => <rect x="10" y="10" width="80" height="80" rx="4" fill={color} />, nameEn: "square" },
  { name: "مثلث", svg: (color: string) => <polygon points="50,5 95,90 5,90" fill={color} />, nameEn: "triangle" },
  { name: "نجمة", svg: (color: string) => <polygon points="50,5 61,35 95,35 68,55 79,90 50,68 21,90 32,55 5,35 39,35" fill={color} />, nameEn: "star" },
  { name: "قلب", svg: (color: string) => <path d="M50 88 C25 65 5 50 5 30 C5 12 18 2 30 2 C40 2 48 10 50 15 C52 10 60 2 70 2 C82 2 95 12 95 30 C95 50 75 65 50 88Z" fill={color} />, nameEn: "heart" },
];

const COLORS = [
  { name: "أحمر", hex: "#ef4444", nameEn: "red" },
  { name: "أزرق", hex: "#3b82f6", nameEn: "blue" },
  { name: "أخضر", hex: "#22c55e", nameEn: "green" },
  { name: "أصفر", hex: "#eab308", nameEn: "yellow" },
  { name: "بنفسجي", hex: "#a855f7", nameEn: "purple" },
  { name: "برتقالي", hex: "#f97316", nameEn: "orange" },
];

type QuestionType = "shape" | "color";

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

const generateQuestion = () => {
  const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  const type: QuestionType = Math.random() > 0.5 ? "shape" : "color";

  if (type === "shape") {
    const wrongShapes = shuffleArray(SHAPES.filter((s) => s.nameEn !== shape.nameEn)).slice(0, 3);
    const options = shuffleArray([shape.name, ...wrongShapes.map((s) => s.name)]);
    return { shape, color, type, question: "ما اسم هذا الشكل؟", answer: shape.name, options };
  } else {
    const wrongColors = shuffleArray(COLORS.filter((c) => c.nameEn !== color.nameEn)).slice(0, 3);
    const options = shuffleArray([color.name, ...wrongColors.map((c) => c.name)]);
    return { shape, color, type, question: "ما لون هذا الشكل؟", answer: color.name, options };
  }
};

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function ColorShapesGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [data, setData] = useState(generateQuestion);
  const [selected, setSelected] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const { speak, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (selected || round >= totalRounds) return;
      const normalizeAr = (s: string) => s.replace(/[^\u0600-\u06FF\s]/g, "").trim();
      const spoken = normalizeAr(transcript);
      const match = data.options.find(opt => {
        const n = normalizeAr(opt);
        return spoken.includes(n) || n.includes(spoken);
      });
      if (match) handleSelect(match);
    },
  });

  const handleSelect = (opt: string) => {
    if (selected) return;
    setSelected(opt);
    if (opt === data.answer) {
      setScore((s) => s + 10);
      encourageCorrect();
    } else {
      encourageWrong();
    }
    setTimeout(() => {
      setSelected(null);
      setRound((r) => r + 1);
      setData(generateQuestion());
    }, timeout);
  };

  const reset = () => {
    setScore(0);
    setRound(0);
    setData(generateQuestion());
    setSelected(null);
    setCompleted(false);
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 10;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ rotate: [0, 360] }} transition={{ repeat: Infinity, duration: 4, ease: "linear" }} className="text-6xl">
              🎨
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">رائع! 🌈</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 10)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-red-500" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round}
          initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, scale: 0.5, rotate: 15 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-orange-400 to-red-500 p-6 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full" onClick={() => speak(data.question + ". الخيارات: " + data.options.join("، "))}>
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button variant={isListening ? "destructive" : "ghost"} size="icon" className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`} onClick={() => isListening ? stopListening() : startListening()} disabled={!!selected}>
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-lg font-medium mb-4">{data.question}</p>
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                  className="flex justify-center"
                >
                  <svg width="120" height="120" viewBox="0 0 100 100">
                    {data.shape.svg(data.color.hex)}
                  </svg>
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt, i) => {
          const showResult = selected !== null;
          const isAnswer = opt === data.answer;
          const isSelected = selected === opt;
          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          // Show color dot for color questions
          const colorDot = data.type === "color" ? COLORS.find((c) => c.name === opt) : null;

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-4 text-center transition-all flex items-center justify-center gap-2 ${bgClass}`}
            >
              {colorDot && (
                <span className="w-5 h-5 rounded-full inline-block shrink-0" style={{ backgroundColor: colorDot.hex }} />
              )}
              <span className="text-lg font-bold text-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
